import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import driversService from "../../services/drivers";

// Recenters the map when the target position changes.
function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

const driverIcon = L.divIcon({
  className: "mq-driver-marker",
  html:
    '<div style="width:18px;height:18px;background:#12162a;border:3px solid #FFDB6B;border-radius:50%;box-shadow:0 0 0 5px rgba(255,219,107,.45)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const dotIcon = (color) =>
  L.divIcon({
    className: "mq-dot-marker",
    html: `<div style="width:12px;height:12px;background:${color};border:2px solid #fff;border-radius:50%"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });

const LIVE_POLL_MS = 20000;
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function DriverLiveMap({ driverId, initialLocation, driverName }) {
  const [mode, setMode] = useState("live"); // 'live' | 'replay'

  // Live state
  const [livePos, setLivePos] = useState(
    initialLocation?.latitude != null
      ? { lat: initialLocation.latitude, lng: initialLocation.longitude }
      : null
  );
  const [lastUpdated, setLastUpdated] = useState(initialLocation?.timestamp || null);

  // Replay state
  const [replayDate, setReplayDate] = useState(todayStr());
  const [track, setTrack] = useState([]);
  const [replayIdx, setReplayIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const playRef = useRef(null);

  // ---- Live polling ----
  useEffect(() => {
    if (mode !== "live") return undefined;
    let active = true;

    const poll = async () => {
      try {
        const driver = await driversService.getDriverById(driverId);
        const loc = driver?.driverProfile?.currentLocation;
        if (active && loc?.latitude != null) {
          setLivePos({ lat: loc.latitude, lng: loc.longitude });
          setLastUpdated(loc.timestamp || new Date().toISOString());
        }
      } catch (e) {
        /* keep polling */
      }
    };

    const id = setInterval(poll, LIVE_POLL_MS);
    poll();
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [mode, driverId]);

  // ---- Load replay track ----
  const loadTrack = useCallback(async () => {
    setLoadingTrack(true);
    try {
      const res = await driversService.getTrack(driverId, { date: replayDate, limit: 5000 });
      setTrack(res.points || []);
      setReplayIdx(0);
      setPlaying(false);
    } catch (e) {
      setTrack([]);
    } finally {
      setLoadingTrack(false);
    }
  }, [driverId, replayDate]);

  useEffect(() => {
    if (mode === "replay") loadTrack();
  }, [mode, loadTrack]);

  // ---- Replay animation ----
  useEffect(() => {
    if (!playing || track.length === 0) return undefined;
    playRef.current = setInterval(() => {
      setReplayIdx((i) => {
        if (i >= track.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, 400);
    return () => clearInterval(playRef.current);
  }, [playing, track]);

  const polyline = useMemo(() => track.map((p) => [p.latitude, p.longitude]), [track]);
  const replayPos = track[replayIdx]
    ? { lat: track[replayIdx].latitude, lng: track[replayIdx].longitude }
    : null;

  const center = mode === "live" ? livePos : replayPos || polyline[0] && { lat: polyline[0][0], lng: polyline[0][1] };
  const fallback = { lat: 24.8607, lng: 67.0011 }; // Karachi fallback
  const mapCenter = center || fallback;

  return (
    <div>
      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => setMode("live")}
          style={tabStyle(mode === "live")}
        >
          ● Live
        </button>
        <button
          onClick={() => setMode("replay")}
          style={tabStyle(mode === "replay")}
        >
          Route Replay
        </button>
        {mode === "live" && lastUpdated && (
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#8a8f9c", alignSelf: "center" }}>
            Updated {new Date(lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Replay controls */}
      {mode === "replay" && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <input
            type="date"
            value={replayDate}
            max={todayStr()}
            onChange={(e) => setReplayDate(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd" }}
          />
          <button onClick={() => setPlaying((p) => !p)} disabled={track.length === 0} style={ctrlBtn}>
            {playing ? "⏸ Pause" : "▶ Play"}
          </button>
          <input
            type="range"
            min={0}
            max={Math.max(0, track.length - 1)}
            value={replayIdx}
            onChange={(e) => setReplayIdx(Number(e.target.value))}
            style={{ flex: 1, minWidth: 120 }}
            disabled={track.length === 0}
          />
          <span style={{ fontSize: 12, color: "#8a8f9c" }}>
            {loadingTrack
              ? "Loading…"
              : track.length === 0
              ? "No track for this day"
              : `${replayIdx + 1}/${track.length}` +
                (track[replayIdx]?.recordedAt
                  ? ` • ${new Date(track[replayIdx].recordedAt).toLocaleTimeString()}`
                  : "")}
          </span>
        </div>
      )}

      <div style={{ height: 360, borderRadius: 8, overflow: "hidden" }}>
        <MapContainer center={[mapCenter.lat, mapCenter.lng]} zoom={15} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Recenter center={[mapCenter.lat, mapCenter.lng]} />

          {mode === "live" && livePos && <Marker position={[livePos.lat, livePos.lng]} icon={driverIcon} />}

          {mode === "replay" && polyline.length > 1 && (
            <>
              <Polyline positions={polyline} pathOptions={{ color: "#F2B845", weight: 4 }} />
              <Marker position={polyline[0]} icon={dotIcon("#34C759")} />
              <Marker position={polyline[polyline.length - 1]} icon={dotIcon("#FF3B30")} />
              {replayPos && <Marker position={[replayPos.lat, replayPos.lng]} icon={driverIcon} />}
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
}

const tabStyle = (active) => ({
  padding: "8px 18px",
  borderRadius: 999,
  border: "1px solid #e0e0e0",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
  background: active ? "#12162a" : "#fff",
  color: active ? "#ffdb6b" : "#555",
});

const ctrlBtn = {
  padding: "6px 14px",
  borderRadius: 8,
  border: "1px solid #ddd",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
};
